// Single source of truth for all CRS services.
// Used by the mega-menu, services hub, service cards, cross-sells, and route generation.

import {
  Hammer,
  Layers,
  Home as HomeIcon,
  Wrench,
  Droplets,
  Wind,
  Flame,
  Gauge,
  Thermometer,
  Sun,
  ShieldAlert,
  type LucideIcon,
} from "lucide-react";

export type ServiceCategory = "insulation" | "structural" | "heating";
export type CheckBrand = "HDC" | "HIC" | "HRC" | "HGC";

export interface Service {
  slug: string;
  name: string;
  shortName: string;
  category: ServiceCategory;
  icon: LucideIcon;
  tagline: string;
  hero: string;
  hero800?: string;
  hero1280?: string;
  intro: string[];
  trustClaims: string[];
  benefits: string[];
  scienceTitle: string;
  scienceBody: string[];
  scienceImage: string;
  scienceImageAlt: string;
  problemTitle: string;
  problemPoints: string[];
  processSteps: { title: string; body: string }[];
  faqs: { q: string; a: string }[];
  crossSell: string[]; // slugs
  referralBrand: CheckBrand;
  referralCopy: string;
  metaDescription: string;
}


import cavityRemovalAsset from "@/assets/services/card-cavity-wall-removal.png.asset.json";
import cavityInsulationAsset from "@/assets/services/card-cavity-wall-insulation.png.asset.json";
import loftInsulationAsset from "@/assets/services/card-loft-insulation.png.asset.json";
import internalWallAsset from "@/assets/services/card-internal-wall-insulation.png.asset.json";
import wallTiesAsset from "@/assets/services/card-remedial-wall-ties.png.asset.json";
import dampMouldAsset from "@/assets/services/card-damp-mould.png.asset.json";
import heatingControlsAsset from "@/assets/services/card-heating-controls.webp.asset.json";
import boilersHeatingAsset from "@/assets/services/card-boilers-heating.png.asset.json";
import dmevVentilationAsset from "@/assets/services/card-ventilation-extraction.png.asset.json";
import ashpAsset from "@/assets/services/card-ashp-v2.jpeg.asset.json";
import solarAsset from "@/assets/services/card-solar-v2.jpeg.asset.json";

import cavityRemoval800 from "@/assets/services/cavity-wall-removal-800.webp";
import cavityRemoval1280 from "@/assets/services/cavity-wall-removal-1280.webp";
import cavityInsulation800 from "@/assets/services/cavity-wall-insulation-800.webp";
import cavityInsulation1280 from "@/assets/services/cavity-wall-insulation-1280.webp";
import loftInsulation800 from "@/assets/services/loft-insulation-800.webp";
import loftInsulation1280 from "@/assets/services/loft-insulation-1280.webp";
import internalWall800 from "@/assets/services/internal-wall-insulation-800.webp";
import internalWall1280 from "@/assets/services/internal-wall-insulation-1280.webp";
import wallTies800 from "@/assets/services/wall-ties-800.webp";
import wallTies1280 from "@/assets/services/wall-ties-1280.webp";
import dampMould800 from "@/assets/services/damp-mould-800.webp";
import dampMould1280 from "@/assets/services/damp-mould-1280.webp";
import dmevVentilation800 from "@/assets/services/dmev-ventilation-800.webp";
import dmevVentilation1280 from "@/assets/services/dmev-ventilation-1280.webp";
import solar800 from "@/assets/services/solar-800.webp";
import solar1280 from "@/assets/services/solar-1280.webp";
import boilersHeating800 from "@/assets/services/boilers-heating-800.webp";
import boilersHeating1280 from "@/assets/services/boilers-heating-1280.webp";
import heatingControls800 from "@/assets/services/heating-controls-800.webp";
import heatingControls1280 from "@/assets/services/heating-controls-1280.webp";
import ashp800 from "@/assets/services/ashp-800.webp";
import ashp1280 from "@/assets/services/ashp-1280.webp";

const cavityRemovalImg = cavityRemovalAsset.url;
const cavityInsulationImg = cavityInsulationAsset.url;
const loftInsulationImg = loftInsulationAsset.url;
const internalWallImg = internalWallAsset.url;
const wallTiesImg = wallTiesAsset.url;
const dampMouldImg = dampMouldAsset.url;
const heatingControlsImg = heatingControlsAsset.url;
const boilersHeatingImg = boilersHeatingAsset.url;
const dmevVentilationImg = dmevVentilationAsset.url;
const ashpImg = ashpAsset.url;
const solarImg = solarAsset.url;

export const SERVICES: Service[] = [
  {
    slug: "cavity-wall-insulation-removal",
    name: "Cavity Wall Insulation Removal & Upgrade",
    shortName: "Cavity Wall Insulation Removal",
    category: "insulation",
    icon: Layers,
    tagline:
      "Old or failed cavity wall insulation could be costing you money, and damaging your home. We remove and replace across Wales and the South of England.",
    hero: cavityRemovalImg,
    hero800: cavityRemoval800,
    hero1280: cavityRemoval1280,
    intro: [
      "Failed or saturated cavity wall insulation is one of the most common causes of damp, mould and rocketing energy bills in homes built after the 1920s. When the original installation has slumped, become wet or was simply the wrong material for the property, the only effective fix is full extraction followed by a like-for-like or improved replacement.",
      "CRS is a PAS 2030 certified contractor with a dedicated extraction team operating across Wales and the South of England. We use industry-leading vacuum equipment to clear every cavity completely, then re-fill with certified bonded bead insulation backed by a 25-year independent guarantee.",
    ],
    trustClaims: [
      "PAS 2030 Certified",
      "25-year independent guarantee",
      "We won't be beaten on quality and price",
    ],
    benefits: [
      "Eliminates cold spots, damp patches and mould caused by failed cavity insulation",
      "Restores the cavity to a dry, performing thermal break in a single visit",
      "Replaced with certified bonded bead, backed by a 25-year independent guarantee",
      "Borescope verification before re-fill, with photographic sign-off on completion",
      "Most semi-detached homes completed in a single working day",
    ],
    scienceTitle: "How cavity wall extraction works",
    scienceBody: [
      "Most failed cavities contain mineral wool fibre or polystyrene bead that has slumped, absorbed moisture, or bridged the cavity entirely. Once that happens, the cavity stops being a thermal break and becomes a moisture pathway: pulling cold and damp directly into your internal walls.",
      "Our teams drill a precise pattern of small access holes, then use high-power vacuum extraction to remove every gram of failed material. The cavity is inspected with borescope cameras to confirm complete clearance before we re-fill with new certified bonded bead, a graphite-enhanced product that delivers superior thermal performance and won't slump or wick water.",
    ],
    scienceImage: rasterSrc("stock/photo-1503387762-592deb58ef4e"),
    scienceImageAlt: "Insulation extraction equipment on a residential project",
    problemTitle: "Don't ignore the signs",
    problemPoints: [
      "Cold spots, damp patches or mould on internal walls",
      "Higher heating bills despite the same usage",
      "Black staining around external wall plug sockets and skirtings",
      "Visible insulation debris falling from air bricks or vents",
      "A previous installation was rejected by your insurer or surveyor",
    ],
    processSteps: [
      { title: "Inspect", body: "Free site survey including borescope inspection of existing cavity material." },
      { title: "Recommend", body: "Written report identifying failed insulation and the right replacement system." },
      { title: "Extract", body: "Vacuum extraction across the full elevation in a single visit where possible." },
      { title: "Re-fill", body: "certified bonded bead reinstated, holes made good, 25-year guarantee issued." },
    ],
    faqs: [
      {
        q: "Why does my cavity wall insulation need to be removed?",
        a: "If insulation has slumped, absorbed water or was the wrong product for your wall type, it stops insulating and starts causing damp. Removal is the only way to restore the cavity to a dry, performing thermal break.",
      },
      {
        q: "How is cavity wall insulation removed?",
        a: "We drill a small pattern of access holes (typically 25mm), then use industrial vacuum extraction to clear the cavity. We use borescope cameras to confirm full clearance before re-filling.",
      },
      {
        q: "How will I know that the faulty insulation has been removed?",
        a: "Every project ends with a borescope inspection and a written sign-off. If you have requested it, we can also provide before/after photographs of every cavity inspected.",
      },
      {
        q: "How long does the work take?",
        a: "Most semi-detached and terraced homes are completed in a single working day. Larger detached properties may take two days.",
      },
    ],
    crossSell: ["cavity-wall-insulation", "internal-wall-insulation", "damp-and-mould-remediation"],
    referralBrand: "HIC",
    referralCopy:
      "Not sure if your cavity insulation has failed? A Home Insulation Check survey will diagnose the issue before any work is quoted.",
    metaDescription:
      "PAS 2030 certified cavity wall insulation removal and upgrade across Wales and the South of England. 25-year guarantee, certified bonded bead replacement.",
  },
  {
    slug: "loft-insulation",
    name: "Loft Insulation",
    shortName: "Loft Insulation",
    category: "insulation",
    icon: HomeIcon,
    tagline: "Up to 25% of your home's heat is lost through an uninsulated loft. We inspect and install across Wales and the South of England.",
    hero: loftInsulationImg,
    hero800: loftInsulation800,
    hero1280: loftInsulation1280,
    intro: [
      "Topping up or installing loft insulation is one of the highest-impact, lowest-disruption upgrades a homeowner can make. Done properly, it pays for itself in a handful of winters and meaningfully cuts your carbon footprint.",
      "CRS installs loft insulation to current building regulation depths (270mm) using accredited materials and methods, with full eaves ventilation detailing to keep your roof structure dry.",
    ],
    trustClaims: [
      "30-year independent guarantee",
      "Installed to current building regulations",
      "TrustMark registered installer",
    ],
    benefits: [
      "Cuts up to 25% of heat lost through an uninsulated loft",
      "Installed to current building regulation depth (270mm) using accredited materials",
      "Full eaves ventilation detailing prevents condensation in the roof void",
      "Pays for itself in a handful of winters through lower heating bills",
      "30-year independent guarantee on every install",
    ],
    scienceTitle: "Why loft insulation matters",
    scienceBody: [
      "Heat rises. In an uninsulated home, a quarter of all the warmth you pay for escapes straight through the loft and into the sky. A 270mm blanket of mineral wool insulation at ceiling level traps that heat where you want it: in your living spaces.",
      "Crucially, loft insulation must be installed with proper ventilation paths at the eaves. Without that, condensation builds in the roof void and you trade an energy problem for a damp problem. Every CRS install includes the correct ventilation detailing.",
    ],
    scienceImage: loftInsulationImg,
    scienceImageAlt: "New loft insulation rolled between joists",
    problemTitle: "Signs your loft insulation needs attention",
    problemPoints: [
      "Existing insulation is less than 200mm deep",
      "Compressed, slumped or visibly damp insulation",
      "Cold upstairs rooms or ice forming on the inside of windows",
      "Sudden jumps in your winter heating bill",
      "Insulation that pre-dates 2003 and has never been topped up",
    ],
    processSteps: [
      { title: "Inspect", body: "Loft access survey to assess existing depth, ventilation and any damp issues." },
      { title: "Recommend", body: "Proposal sized to your loft area with ventilation detailing included." },
      { title: "Install", body: "Mineral wool laid to 270mm depth, eaves vents fitted, loft hatch insulated." },
      { title: "Sign-off", body: "Photographic record of works and 30-year guarantee certificate issued." },
    ],
    faqs: [
      { q: "How thick should loft insulation be?", a: "Current building regulations recommend 270mm of mineral wool at ceiling level, which is what we install as standard." },
      { q: "Will loft insulation cause damp?", a: "Not when installed properly. We always include eaves ventilation paths so warm air from your home cannot condense in the cold roof space." },
      { q: "Can you insulate around stored items?", a: "We can fit a raised storage deck above the insulation if you need to keep using your loft for storage. Ask for it on the survey." },
      { q: "How long does it take?", a: "A standard three-bedroom semi takes around half a day from arrival to sign-off." },
    ],
    crossSell: ["cavity-wall-insulation", "ventilation-and-extraction", "heating-controls"],
    referralBrand: "HIC",
    referralCopy: "Not sure what you've got up there? A Home Insulation Check survey measures existing depth and condition so you only pay for what you actually need.",
    metaDescription: "Loft insulation installed to 270mm building reg depth with eaves ventilation. 30-year guarantee from CRS Energy in Wales & the South of England.",
  },
  {
    slug: "cavity-wall-insulation",
    name: "Cavity Wall Insulation",
    shortName: "Cavity Wall Insulation",
    category: "insulation",
    icon: Layers,
    tagline: "Properly installed cavity wall insulation cuts heating bills and reduces condensation. Certified installs across Wales and the South of England.",
    hero: cavityInsulationImg,
    hero800: cavityInsulation800,
    hero1280: cavityInsulation1280,
    intro: [
      "Around a third of all heat lost from an uninsulated home escapes through the walls. For most properties built between 1920 and the early 1990s, filling the cavity is the single most cost-effective way to bring that loss down.",
      "CRS installs certified bonded bead cavity wall insulation, a graphite-enhanced product suited to almost every property type, including those exposed to driving rain. Our installers are PAS 2030 certified and every install carries a 25-year independent guarantee.",
    ],
    trustClaims: [
      "certified bonded bead system",
      "25-year independent guarantee",
      "PAS 2030 certified installation",
    ],
    benefits: [
      "Reduces heat loss through walls by around a third in suitable properties",
      "Graphite-enhanced bonded bead delivers superior U-values versus older mineral wool",
      "Won't slump or wick water across the cavity over time",
      "PAS 2030 certified install with a 25-year independent guarantee",
      "Holes pointed to match existing mortar, virtually invisible from the kerb",
    ],
    scienceTitle: "How cavity wall insulation works",
    scienceBody: [
      "A typical UK cavity wall has a 50-100mm gap between the inner and outer leaf. Left empty, that air gap is a poor insulator and a major route for heat loss. Filling the gap with bonded bead creates a continuous thermal break that dramatically slows the rate at which heat moves outward.",
      "Bonded bead beats older mineral wool fill on three counts: it doesn't slump, it doesn't wick water across the cavity, and the graphite-enhanced version delivers a measurably better U-value. We assess every property for suitability before we quote.",
    ],
    scienceImage: rasterSrc("stock/photo-1581094288338-2314dddb7ece"),
    scienceImageAlt: "Cavity wall insulation install in progress",
    problemTitle: "Is your home a candidate?",
    problemPoints: [
      "Property built between 1920 and 1995 with masonry walls",
      "Cavity is empty or has never been insulated",
      "Cold internal wall surfaces, especially north-facing rooms",
      "Higher than expected heating bills",
      "Mild condensation on internal walls in winter",
    ],
    processSteps: [
      { title: "Inspect", body: "Free borescope survey to confirm cavity width, condition and suitability." },
      { title: "Recommend", body: "Written specification with thermal performance figures and price." },
      { title: "Install", body: "Bonded bead injected through a precise drill pattern in a single visit." },
      { title: "Make good", body: "Holes filled to match your mortar colour, 25-year guarantee issued." },
    ],
    faqs: [
      { q: "Will it cause damp?", a: "Not when the cavity has been properly assessed first. We refuse to install where rendering, pointing or DPC issues mean the wall is not suitable. That assessment is part of the free survey." },
      { q: "How long does the install take?", a: "Most semi-detached homes are done in a single day." },
      { q: "What's the difference between bead and mineral wool?", a: "Bonded bead doesn't slump over time, doesn't wick water, and delivers slightly better thermal performance. It's the modern standard." },
      { q: "Will it affect my walls visually?", a: "Drill holes are typically 22-25mm, plugged with mortar matched to your existing pointing. From a few metres away, you won't see them." },
    ],
    crossSell: ["loft-insulation", "internal-wall-insulation", "ventilation-and-extraction"],
    referralBrand: "HIC",
    referralCopy: "Not sure if your home is suitable? Home Insulation Check provides an independent survey before you commit to any work.",
    metaDescription: "certified bonded bead cavity wall insulation by PAS 2030 certified installers. 25-year guarantee. CRS Energy across Wales and the South of England.",
  },
  {
    slug: "internal-wall-insulation",
    name: "Internal Wall Insulation",
    shortName: "Internal Wall Insulation",
    category: "insulation",
    icon: Layers,
    tagline: "For solid-wall homes across Wales and the South of England, internal insulation transforms thermal performance.",
    hero: internalWallImg,
    hero800: internalWall800,
    hero1280: internalWall1280,
    intro: [
      "Around a fifth of UK homes were built with solid masonry walls and no cavity to fill. Without intervention they remain among the hardest properties to keep warm, and the most expensive to heat.",
      "Internal wall insulation (IWI) lines the inside face of external walls with a high-performance insulation board, finished in fresh plaster and ready to redecorate. CRS specialist dry-lining teams complete most rooms within a day per elevation, with full attention to vapour control and the elimination of cold bridging at junctions.",
    ],
    trustClaims: [
      "PAS 2030 certified",
      "Specialist dry-lining teams",
      "Vapour-control detailing as standard",
    ],
    benefits: [
      "Transforms the thermal performance of solid-wall homes that can't take cavity fill",
      "Eliminates cold internal walls, mould in corners and at reveals",
      "Specialist vapour-control detailing protects the masonry behind",
      "Suitable for conservation, listed and planning-restricted properties",
      "Phased room-by-room installs available to suit your budget",
    ],
    scienceTitle: "How internal wall insulation works",
    scienceBody: [
      "Solid walls lose heat at roughly twice the rate of cavity walls. Lining the internal face with insulation board (typically 60-100mm depending on the system specified) creates a new thermal layer between you and the cold masonry behind.",
      "The critical engineering detail is vapour control. Done badly, IWI can trap moisture against the back of the insulation and rot the wall over time. Every CRS install uses the correct vapour-control layer for the system specified, plus careful detailing at floors, ceilings, reveals and junctions to break the cold bridging that causes mould.",
    ],
    scienceImage: internalWallImg,
    scienceImageAlt: "Internal wall insulation board being installed",
    problemTitle: "When IWI is the right answer",
    problemPoints: [
      "Pre-1920s solid masonry property",
      "External wall insulation isn't permitted (conservation, listed, planning)",
      "Cold internal walls, mould in corners or on reveals",
      "Heating system can't keep up despite working correctly",
      "Major room refurbishment already planned",
    ],
    processSteps: [
      { title: "Inspect", body: "Survey of wall construction, existing damp condition and room layouts." },
      { title: "Specify", body: "System chosen for performance and disruption, with vapour control detailed." },
      { title: "Install", body: "Boards mechanically fixed and sealed, walls re-plastered ready to decorate." },
      { title: "Sign-off", body: "Photographic record and post-completion thermal images on request." },
    ],
    faqs: [
      { q: "Will I lose room space?", a: "A typical IWI build-up takes around 80-100mm off internal dimensions. Most clients accept that against the comfort and bill savings." },
      { q: "Can you do one room at a time?", a: "Yes. IWI is well suited to phased works: for example, the coldest room first, then the rest later." },
      { q: "Will I need to redecorate?", a: "Yes. We finish to a clean plaster surface, ready for paint or paper." },
      { q: "How disruptive is it?", a: "Furniture needs to be moved out and skirtings, sockets and radiators temporarily removed. We coordinate the sequencing room by room." },
    ],
    crossSell: ["damp-and-mould-remediation", "ventilation-and-extraction", "loft-insulation"],
    referralBrand: "HIC",
    referralCopy: "Not sure if IWI is right for your property? A Home Insulation Check survey will model the options and costs before you commit.",
    metaDescription: "PAS 2030 internal wall insulation for solid-wall homes. Specialist dry-lining teams, vapour-control detailing and full make-good. CRS Energy.",
  },
  {
    slug: "remedial-wall-ties",
    name: "Remedial Wall Tie Installation",
    shortName: "Remedial Wall Ties",
    category: "structural",
    icon: Wrench,
    tagline:
      "Failed wall ties compromise your home's structural integrity. Replacement is faster than you think.",
    hero: wallTiesImg,
    hero800: wallTies800,
    hero1280: wallTies1280,
    intro: [
      "Wall ties hold the inner and outer leaves of a cavity wall together. When the original ties corrode and fail, typically in homes built before 1981, the outer leaf can begin to bow, crack or move independently, with serious structural consequences if left unaddressed.",
      "CRS is a PAM Ties authorised contractor. We replace failed ties with stainless-steel remedial ties installed through the outer leaf, restoring full structural connection without the disruption of taking down the wall. Most domestic properties are completed in less than a day with a 30-year guarantee.",
    ],
    trustClaims: [
      "PAM Ties authorised contractor",
      "30-year guarantee on all installations",
      "We won't be beaten on quality and price",
    ],
    benefits: [
      "Restores full structural connection between inner and outer wall leaves",
      "Stainless-steel ties resin-fixed, a permanent, corrosion-proof solution",
      "PAM Ties authorised contractor with a 30-year guarantee on every install",
      "Most domestic properties completed in less than a day",
      "Holes pointed to match existing mortar, work is virtually invisible",
    ],
    scienceTitle: "Why wall ties fail",
    scienceBody: [
      "Wall ties built before 1981 were generally manufactured from mild steel with a thin galvanised coating. Over decades, that coating breaks down and the steel corrodes, and as it corrodes, it expands, exerting pressure on the surrounding mortar and gradually pushing the outer leaf away from the inner leaf.",
      "The signs are usually horizontal cracks running along the mortar bed at regular vertical intervals (matching the original tie spacing), bulging brickwork, or movement around openings. Once spotted, replacement is straightforward, but ignored, the failure accelerates.",
    ],
    scienceImage: wallTiesImg,
    scienceImageAlt: "Brick wall close-up showing mortar joints",
    problemTitle: "Signs of wall tie failure",
    problemPoints: [
      "Horizontal cracks at regular intervals along the mortar beds",
      "Bulging or bowing of the outer brickwork",
      "Brickwork that has visibly moved away from window or door frames",
      "Property built before 1981 with no tie history",
      "A surveyor or insurer has flagged tie failure",
    ],
    processSteps: [
      { title: "Inspect", body: "Survey including localised endoscopic inspection of existing ties." },
      { title: "Recommend", body: "Written specification with tie type, spacing and quantity." },
      { title: "Install", body: "Stainless-steel remedial ties drilled and resin-fixed through the outer leaf." },
      { title: "Make good", body: "Holes pointed to match existing mortar, 30-year guarantee issued." },
    ],
    faqs: [
      { q: "How long does it take?", a: "A typical semi-detached property is completed in a single day. Larger or more severely affected properties may take two." },
      { q: "Will the work be visible?", a: "Holes are around 14mm and are pointed back to match your mortar. From a couple of metres they are not visible." },
      { q: "Do you remove the old ties?", a: "Where they are accessible we cut them flush. The new stainless-steel ties take over the structural role." },
      { q: "Is scaffolding required?", a: "For most two-storey domestic properties yes: we include it in the quote." },
    ],
    crossSell: ["cavity-wall-insulation-removal", "damp-and-mould-remediation", "cavity-wall-insulation"],
    referralBrand: "HDC",
    referralCopy: "Cracking or movement also showing damp? Home Damp Check will diagnose whether the two issues are linked before any work is specified.",
    metaDescription: "PAM Ties authorised remedial wall tie replacement by CRS Energy. Stainless-steel ties, 30-year guarantee, most homes completed in a single day.",
  },
  {
    slug: "damp-and-mould-remediation",
    name: "Damp & Mould Remediation",
    shortName: "Damp & Mould Remediation",
    category: "structural",
    icon: Droplets,
    tagline: "From penetrating damp to chronic mould, diagnosed, treated and prevented across Wales and the South of England.",
    hero: dampMouldImg,
    hero800: dampMould800,
    hero1280: dampMould1280,
    intro: [
      "Damp and mould are rarely simple. The visible problem is almost always a symptom of something else: failed cavity insulation, blocked DPC, inadequate ventilation, a roof or guttering defect, or condensation from modern lifestyles meeting older building fabric.",
      "CRS works directly from Home Damp Check survey reports (or your own) to remediate the cause, treat the affected surfaces, and put preventative measures in place. We carry full liability cover, follow PAS 2035 principles for retrofit assessment, and verify our work post-completion.",
    ],
    trustClaims: [
      "Works to HDC survey reports or your own",
      "Full liability and product cover",
      "Post-works verification included",
    ],
    benefits: [
      "Treats the cause of damp, not just the visible symptom",
      "Independent diagnosis available via our sister brand Home Damp Check",
      "Full liability and product cover with post-works moisture verification",
      "Safe biocides, suitable for households with children and pets",
      "Coordinated with insulation, ventilation and structural works where needed",
    ],
    scienceTitle: "Treating cause, not symptom",
    scienceBody: [
      "Painting over mould is a 90-day fix. Without addressing whatever is putting moisture into that wall, ceiling or corner, the mould comes back, often worse, often somewhere new.",
      "Our process always starts with diagnosis: where is the moisture coming from, and why is it landing here? Only then do we specify the remediation: which can range from cavity extraction or DPC injection to bathroom extraction upgrades or PIV ventilation. Treatment of the visible mould happens last, not first.",
    ],
    scienceImage: dampMouldImg,
    scienceImageAlt: "Damp affected internal wall before remediation",
    problemTitle: "Don't ignore the signs",
    problemPoints: [
      "Persistent black mould in corners, on ceilings or behind furniture",
      "Tide marks at low level on internal or external walls",
      "Salt staining or blistering plaster",
      "Peeling paint, swollen skirtings or musty smells",
      "A health condition that mould is making worse",
    ],
    processSteps: [
      { title: "Diagnose", body: "Site visit (or review of HDC report) to identify the moisture source." },
      { title: "Specify", body: "Written scope addressing the cause, the fabric and the visible damage." },
      { title: "Remediate", body: "Cause works, replastering where needed, anti-mould treatment of surfaces." },
      { title: "Verify", body: "Post-works moisture readings and a written sign-off." },
    ],
    faqs: [
      { q: "Will the mould come back?", a: "Not if we have correctly diagnosed and treated the cause. Treating mould without fixing the cause is the most common reason it returns." },
      { q: "Do I need a survey first?", a: "We strongly recommend it. Home Damp Check is set up specifically to do this independently before any work is quoted." },
      { q: "Is the treatment safe for children and pets?", a: "Yes. The biocides we use are fully tested and we ventilate treated areas before re-occupation." },
      { q: "How long until the room is usable?", a: "Most rooms are back in use within 24 hours of treatment, with full re-decoration possible after the agreed drying period." },
    ],
    crossSell: ["cavity-wall-insulation-removal", "ventilation-and-extraction", "internal-wall-insulation"],
    referralBrand: "HDC",
    referralCopy: "Need an independent damp diagnosis before any work is priced? Home Damp Check provides exactly that.",
    metaDescription: "Damp and mould remediation that treats the cause, not the symptom. CRS Energy works to HDC survey reports across Wales and the South of England.",
  },
  {
    slug: "ventilation-and-extraction",
    name: "Ventilation & Extraction",
    shortName: "Ventilation & Extraction",
    category: "structural",
    icon: Wind,
    tagline:
      "Most modern damp problems are ventilation problems. We fix the cause across Wales and the South of England, not just the symptom.",
    hero: dmevVentilationImg,
    hero800: dmevVentilation800,
    hero1280: dmevVentilation1280,
    intro: [
      "Modern living puts an enormous amount of moisture into a home: showers, cooking, drying clothes, even breathing. In well-sealed modern homes, and well-upgraded older ones, that moisture has nowhere to go without proper ventilation. The result is condensation, mould and damaged fabric.",
      "CRS designs and installs the right ventilation strategy for your property: positive input ventilation (PIV) for whole-house solutions, MEV and MVHR systems for new and retrofit projects, and high-performance kitchen and bathroom extraction where targeted intervention is needed.",
    ],
    trustClaims: [
      "PIV, MEV and MVHR specialists",
      "Kitchen & bathroom extraction upgrades",
      "Building regs Part F compliant installations",
    ],
    benefits: [
      "Cuts indoor humidity by 20-30% and stops condensation forming on cold surfaces",
      "PIV, MEV, MVHR and targeted kitchen/bathroom extraction options",
      "Building regulations Part F compliant, with full commissioning documentation",
      "Quiet, low-running-cost units (typically 5-8W continuous for PIV)",
      "Particularly effective in retrofitted homes with new insulation or glazing",
    ],
    scienceTitle: "Why ventilation is the modern fix",
    scienceBody: [
      "An average family of four puts around 12 litres of water vapour into the air every day. In an old, draughty house that vapour escapes through gaps and chimneys. In a tightened-up modern (or retrofitted) home it has to go somewhere, and if you don't give it a planned route, it lands on the coldest surfaces in your home as condensation.",
      "Mechanical ventilation systems give that moisture a controlled extraction path. Done right, they cut humidity by 20-30%, eliminate condensation on glazing, and stop mould before it starts.",
    ],
    scienceImage: dmevVentilationImg,
    scienceImageAlt: "Wall-mounted DMEV extractor fan unit",
    problemTitle: "When ventilation is the answer",
    problemPoints: [
      "Condensation on bedroom windows every winter morning",
      "Persistent mould in bathrooms despite a working extractor",
      "New double glazing has triggered new damp issues",
      "Recent insulation upgrades have changed how the house behaves",
      "Building regs require mechanical extraction for an extension or refurb",
    ],
    processSteps: [
      { title: "Survey", body: "Whole-house humidity assessment and identification of moisture sources." },
      { title: "Specify", body: "PIV, MEV, MVHR or targeted extraction sized to your property." },
      { title: "Install", body: "Specialist installation including ducting, controls and commissioning." },
      { title: "Commission", body: "Performance test, handover and any required Part F documentation." },
    ],
    faqs: [
      { q: "What's the difference between PIV and MVHR?", a: "PIV gently pushes filtered fresh air into the home from a loft-mounted unit, displacing stale damp air. MVHR extracts and supplies in equal volumes through ducting and recovers heat from the outgoing air. MVHR is more efficient but more invasive to install." },
      { q: "Will it be noisy?", a: "Modern units run at 20-30 dB at the unit: quieter than a fridge. You won't notice them in normal use." },
      { q: "Will it cost a lot to run?", a: "PIV runs at around 5-8W continuous: pennies per day. MVHR is a little higher but typically pays back in heat recovery." },
      { q: "Will it stop condensation completely?", a: "On glazing and most cold surfaces, yes: provided the system is correctly sized and the house has no other unresolved moisture sources." },
    ],
    crossSell: ["damp-and-mould-remediation", "loft-insulation", "internal-wall-insulation"],
    referralBrand: "HDC",
    referralCopy: "Damp problem and not sure if ventilation is the right fix? Home Damp Check will diagnose before any work is specified.",
    metaDescription: "PIV, MEV, MVHR and kitchen or bathroom extraction installed to Building Regs Part F by CRS Energy across Wales and the South of England.",
  },
  {
    slug: "solar-pv",
    name: "Solar PV",
    shortName: "Solar PV",
    category: "heating",
    icon: Sun,
    tagline: "Generate your own electricity. Cut your bills. Future-proof your home, across Wales and the South of England.",
    hero: solarImg,
    hero800: solar800,
    hero1280: solar1280,
    intro: [
      "Solar photovoltaic (PV) systems generate electricity directly from daylight, even on cloudy days: feeding it straight into your home and selling any surplus back to the grid. Combined with a battery, you can store daytime generation for evening use and dramatically cut your reliance on bought-in power.",
      "CRS installs solar PV to MCS-aligned standards, with full system design including panel layout, inverter selection, and optional battery storage to suit how your household actually uses electricity.",
    ],
    trustClaims: [
      "MCS-aligned installation",
      "Panel and inverter selection to suit your roof",
      "Battery storage options",
    ],
    benefits: [
      "Generates clean electricity directly from daylight, even on cloudy days",
      "Smart Export Guarantee earns you money for surplus exported to the grid",
      "Optional battery storage shifts daytime generation to evening use",
      "MCS-aligned install with full system design and commissioning",
      "Typical 4kW domestic system covers around half of average household demand",
    ],
    scienceTitle: "How solar PV works for you",
    scienceBody: [
      "Solar panels generate DC electricity, which an inverter converts to AC for use in your home. Anything you don't use can be exported to the grid for payment under the Smart Export Guarantee, or stored in a home battery for later use.",
      "A typical 4kW domestic system in the South West generates around 3,800 kWh per year: enough to cover roughly half of an average household's electricity demand, with the right battery sizing pushing self-consumption higher again.",
    ],
    scienceImage: rasterSrc("stock/photo-1497440001374-f26997328c1b"),
    scienceImageAlt: "Solar PV panels on a residential roof",
    problemTitle: "Is your home suitable?",
    problemPoints: [
      "Roof orientation between south-east and south-west",
      "Limited shading from trees, chimneys or neighbouring buildings",
      "Roof in good structural condition with 15+ years of life left",
      "Daytime electricity use you can shift onto generation",
      "Interest in pairing with a battery and/or heat pump",
    ],
    processSteps: [
      { title: "Survey", body: "Roof and electrical survey, generation modelling and battery sizing." },
      { title: "Design", body: "Panel layout, inverter spec, battery option, written proposal." },
      { title: "Install", body: "Typically 1-2 days for a standard domestic system, scaffolded and tidy." },
      { title: "Commission", body: "Grid connection notification, app set-up, generation handover." },
    ],
    faqs: [
      { q: "How long does it take to pay back?", a: "For a typical domestic system without battery, around 8-10 years at current energy prices. Battery and heat pump pairings shift this further." },
      { q: "What about cloudy days?", a: "Panels still generate in cloudy conditions, typically 10-25% of peak, and UK systems are sized assuming a realistic mix of weather." },
      { q: "Do I need planning permission?", a: "Most domestic installs are permitted development. Listed buildings and conservation areas need separate consideration." },
      { q: "Can I add a battery later?", a: "Yes, but it's usually cheaper to spec the inverter for a battery from day one even if you add the battery later." },
    ],
    crossSell: ["air-source-heat-pumps", "heating-controls", "boilers-and-heating"],
    referralBrand: "HRC",
    referralCopy: "Solar plus battery plus heat pump? Home Renewables Check can model the whole package against your real-world usage.",
    metaDescription: "MCS-aligned solar PV installation with optional battery storage. Independent generation modelling and full commissioning. CRS Energy.",
  },
  {
    slug: "boilers-and-heating",
    name: "Boilers & Heating Systems",
    shortName: "Boilers & Heating",
    category: "heating",
    icon: Flame,
    tagline: "A-rated boilers, full system upgrades, radiator replacement and pipework across Wales and the South of England.",
    hero: boilersHeatingImg,
    hero800: boilersHeating800,
    hero1280: boilersHeating1280,
    intro: [
      "A modern A-rated condensing boiler can cut your gas use by 20-30% compared with a 15-year-old unit, and a properly balanced and zoned system can save more again. CRS handles the full scope: from straight swaps to whole-system overhauls including radiators, pipework and hot water cylinders.",
      "Every install is carried out by Gas Safe registered engineers, comes with the manufacturer's full warranty (typically 7-12 years on leading brands), and is registered with Building Control on completion.",
    ],
    trustClaims: [
      "Gas Safe registered engineers",
      "Full manufacturer warranties",
      "First-time fix promise",
    ],
    benefits: [
      "A-rated condensing boilers cut gas use by 20-30% versus older units",
      "Gas Safe registered engineers with manufacturer warranties up to 12 years",
      "Whole-system survey: pipework, radiators, controls and hot water reviewed",
      "Most installs completed in 1-2 days with full make-good",
      "Building Control notification and warranty registration handled for you",
    ],
    scienceTitle: "Why a modern system matters",
    scienceBody: [
      "Old boilers waste fuel turning water hotter than your radiators need, then cycling on and off all day. A modern condensing boiler matches output to demand and recovers waste heat from its own flue gases, that's where the efficiency gain comes from.",
      "But the boiler is only half the story. Undersized pipework, imbalanced radiators, missing TRVs and the wrong controls can rob a brand-new system of most of its potential. We always survey the whole system, not just the appliance.",
    ],
    scienceImage: boilersHeatingImg,
    scienceImageAlt: "Combination boiler installation",
    problemTitle: "When to replace",
    problemPoints: [
      "Boiler is 12+ years old or breaks down repeatedly",
      "Radiators are cold at the bottom or take ages to warm up",
      "Hot water runs out faster than it should",
      "System pressure won't hold or you bleed radiators constantly",
      "Heating bills are climbing year on year for the same usage",
    ],
    processSteps: [
      { title: "Survey", body: "Free in-home survey covering boiler, system, controls and any upgrades." },
      { title: "Quote", body: "Fixed-price written quote with options across leading manufacturers." },
      { title: "Install", body: "Most installs completed in 1-2 days, with full make-good of disturbed surfaces." },
      { title: "Register", body: "Gas Safe and Building Control notification, warranty registration and handover." },
    ],
    faqs: [
      { q: "Which boiler brands do you fit?", a: "We are accredited installers for Worcester Bosch, Vaillant, Ideal and Baxi, with extended warranties available on each." },
      { q: "Combi or system boiler?", a: "It depends on hot water demand, available space and the existing pipework. We'll talk you through the trade-offs at survey." },
      { q: "Will I need new radiators?", a: "Not always. Where existing radiators are sound we keep them. Where they're undersized or corroded, we'll quote replacement separately so you can choose." },
      { q: "Do you offer finance?", a: "We can introduce finance options on larger installs: ask at survey." },
    ],
    crossSell: ["heating-controls", "air-source-heat-pumps", "solar-pv"],
    referralBrand: "HRC",
    referralCopy: "Considering a heat pump or hybrid system? Home Renewables Check provides an independent survey before you commit to a heating route.",
    metaDescription: "A-rated boiler installs and full heating upgrades by Gas Safe engineers at CRS Energy, with manufacturer warranties of up to 12 years.",
  },
  {
    slug: "heating-controls",
    name: "Heating Controls",
    shortName: "Heating Controls",
    category: "heating",
    icon: Gauge,
    tagline: "Smart controls cut bills without changing your boiler. Fitted and configured across Wales and the South of England.",
    hero: heatingControlsImg,
    hero800: heatingControls800,
    hero1280: heatingControls1280,
    intro: [
      "Most homes are running heating systems built decades ago with controls that haven't kept up. Adding a smart thermostat, room-by-room TRVs and proper zoning can cut a typical gas bill by 10-20%: without touching the boiler itself.",
      "CRS supplies and fits the leading control systems (Hive, Nest, Tado, Drayton Wiser) and configures them around how your household actually uses heat.",
    ],
    trustClaims: [
      "Compatible with most modern systems",
      "Professional installation and pairing",
      "Energy-saving setup guidance included",
    ],
    benefits: [
      "Cuts heating bills by 10-20% without changing your boiler",
      "Compatible with Hive, Nest, Tado, Drayton Wiser and most modern systems",
      "Room-by-room TRVs and zoning so you only heat the rooms you use",
      "Remote control via smartphone app with schedules set up before we leave",
      "Most installs completed in 2-4 hours with minimal disruption",
    ],
    scienceTitle: "Why controls move the needle",
    scienceBody: [
      "Traditional thermostats heat the whole house to one temperature for one schedule. That works when everyone is home all the time, but for most modern households it means heating empty rooms most of the day.",
      "Smart controls combine a learning thermostat with room-level TRVs, so each space gets exactly the temperature it needs at exactly the time it's used. The savings come from heating less, not heating worse.",
    ],
    scienceImage: rasterSrc("stock/photo-1558002038-1055907df827"),
    scienceImageAlt: "Smart heating thermostat on a wall",
    problemTitle: "Signs your controls need an upgrade",
    problemPoints: [
      "Single dial thermostat from the previous decade",
      "No TRVs on radiators, or stuck/non-functional ones",
      "No way to control heating remotely",
      "Whole house heated to the same temperature regardless of use",
      "Boiler runs while you're at work or away",
    ],
    processSteps: [
      { title: "Recommend", body: "Quick visit to confirm boiler and system type, then a control package recommendation." },
      { title: "Supply", body: "Full kit supplied: thermostat, hub, smart TRVs as required." },
      { title: "Install", body: "Most installs completed in 2-4 hours including app set-up." },
      { title: "Configure", body: "Schedules, zones and savings tips set up with you before we leave." },
    ],
    faqs: [
      { q: "Will it work with my boiler?", a: "Almost certainly. Modern smart controls are compatible with the vast majority of UK gas, oil and LPG systems. We confirm at the recommend stage." },
      { q: "Do I need wifi?", a: "Yes: smart controls rely on a home wifi connection to talk to the app." },
      { q: "Will it really save money?", a: "Realistically, 10-20% on heating spend for most households, more for those with empty homes during the day." },
      { q: "Can I keep using it like a normal thermostat?", a: "Yes. The wall unit works like a traditional thermostat for anyone who doesn't want to use the app." },
    ],
    crossSell: ["boilers-and-heating", "loft-insulation", "air-source-heat-pumps"],
    referralBrand: "HGC",
    referralCopy: "Smart controls qualify for funding under some grant schemes. Home Grant Check can confirm if you're eligible.",
    metaDescription: "Smart heating controls supplied and installed: Hive, Nest, Tado, Drayton Wiser. Professional setup and savings configuration. CRS Energy.",
  },
  {
    slug: "air-source-heat-pumps",
    name: "Air Source Heat Pumps",
    shortName: "Air Source Heat Pumps",
    category: "heating",
    icon: Thermometer,
    tagline:
      "The future of home heating, installed and commissioned by accredited engineers across Wales and the South of England.",
    hero: ashpImg,
    hero800: ashp800,
    hero1280: ashp1280,
    intro: [
      "Air source heat pumps extract heat from outside air, even in cold weather, and deliver it into your home at three to four times the energy efficiency of a gas boiler. For the right property, with the right specification and the right radiators, they are the lowest-carbon and increasingly the lowest-cost way to heat a home.",
      "CRS installs heat pumps to MCS-aligned standards, working from independent Home Renewables Check surveys to make sure the spec, the radiator sizing and the controls are all matched to your property's real-world heat loss.",
    ],
    trustClaims: [
      "MCS-aligned installation",
      "HRC survey-led specification",
      "Full handover and commissioning included",
    ],
    benefits: [
      "Three to four times more efficient than a gas boiler in real-world use",
      "MCS-aligned install backed by an independent Home Renewables Check survey",
      "Eligible for the £7,500 Boiler Upgrade Scheme grant in England and Wales (up to £9,000 for oil & LPG homes from 21 July 2026)",
      "Maintains efficiency down to around -15°C, well below typical UK winters",
      "Cuts carbon emissions and protects you from gas-price volatility",
    ],
    scienceTitle: "How heat pumps work",
    scienceBody: [
      "A heat pump uses the same refrigeration cycle as your fridge, but in reverse. A small amount of electrical energy moves a much larger amount of heat from outdoors to indoors: three to four units of heat for every unit of electricity, even at single-digit outside temperatures.",
      "The catch is that they deliver heat at a lower flow temperature than a gas boiler, which means existing radiators may need to be upsized for them to keep the house comfortable. Done properly, they are quieter than a fridge, last 15+ years and dramatically cut carbon emissions.",
    ],
    scienceImage: ashpImg,
    scienceImageAlt: "External air source heat pump unit",
    problemTitle: "When a heat pump makes sense",
    problemPoints: [
      "Boiler is at end of life and you want to plan ahead",
      "Property is reasonably well insulated, or insulation upgrades are planned",
      "Outside space for an external unit (typically wall-mounted)",
      "You want to cut carbon emissions and lock in fuel-cost certainty",
      "You're considering grant funding (BUS or local schemes)",
    ],
    processSteps: [
      { title: "Survey", body: "Independent HRC survey to confirm suitability and specify the system." },
      { title: "Design", body: "MCS-aligned design including radiator sizing and hot water provision." },
      { title: "Install", body: "Typically 3-5 day install with minimal household disruption." },
      { title: "Commission", body: "Performance commissioning, controls walkthrough and full handover pack." },
    ],
    faqs: [
      { q: "Will it work in winter?", a: "Yes. Modern heat pumps maintain efficiency down to around -15°C, well below typical UK winter temperatures." },
      { q: "Will I need new radiators?", a: "Often yes: heat pumps run at lower flow temperatures so existing radiators may need to be upsized. The HRC survey models this in advance." },
      { q: "Are there grants?", a: "Yes. The Boiler Upgrade Scheme currently offers £7,500 toward an air source heat pump install in England and Wales (up to £9,000 for eligible oil and LPG homes from 21 July 2026). Home Grant Check can confirm eligibility." },
      { q: "Will it heat my hot water?", a: "Yes: installed alongside an unvented hot water cylinder for stored hot water." },
    ],
    crossSell: ["solar-pv", "heating-controls", "loft-insulation"],
    referralBrand: "HRC",
    referralCopy: "Heat pumps need a property-specific survey to specify properly. Home Renewables Check is set up to do exactly that.",
    metaDescription: "MCS-aligned air source heat pump installation, HRC survey-led specification and full commissioning. CRS Energy across Wales and the South of England.",
  },
];

export const SERVICES_BY_CATEGORY: Record<ServiceCategory, Service[]> = {
  insulation: SERVICES.filter((s) => s.category === "insulation"),
  structural: SERVICES.filter((s) => s.category === "structural"),
  heating: SERVICES.filter((s) => s.category === "heating"),
};

export const CATEGORY_LABELS: Record<ServiceCategory, string> = {
  insulation: "Insulation",
  structural: "Damp, Structural & Ventilation",
  heating: "Heating & Renewables",
};

export const CATEGORY_ORDER: ServiceCategory[] = ["heating", "structural", "insulation"];

export const CATEGORY_DESCRIPTIONS: Record<ServiceCategory, string> = {
  insulation:
    "Loft, cavity and internal wall insulation: installed, removed, or upgraded to the latest standards.",
  structural:
    "Damp & mould remediation, remedial wall ties, and ventilation & extraction to keep your home dry and healthy.",
  heating:
    "Boilers, heating controls, air source heat pumps and solar PV, modern systems built for lower bills.",
};

import hdcLogo from "@/assets/brands/hdc-logo.webp";
import hicLogo from "@/assets/brands/hic-logo.webp";
import hrcLogo from "@/assets/brands/hrc-logo.webp";
import { rasterSrc } from "@/generated/raster-images";

const hgcLogo = rasterSrc("brands/hgc-logo");

export const CHECK_BRANDS: Record<
  CheckBrand,
  { name: string; tagline: string; url: string; logo: string; phone?: string }
> = {
  HDC: {
    name: "Home Damp Check",
    tagline: "Damp & mould investigation surveys",
    url: "https://homedampcheck.co.uk",
    logo: hdcLogo,
  },
  HIC: {
    name: "Home Insulation Check",
    tagline: "Insulation surveys & efficiency audits",
    url: "https://homeinsulationcheck.co.uk",
    logo: hicLogo,
  },
  HRC: {
    name: "Home Renewables Check",
    tagline: "Heating, heat pump & solar surveys",
    url: "https://homerenewablescheck.co.uk",
    logo: hrcLogo,
  },
  HGC: {
    name: "Home Grant Check",
    tagline:
      "Government-backed heating & insulation grants. Find out if you qualify in 60 seconds.",
    url: "https://www.homegrantcheck.co.uk",
    logo: hgcLogo,
    phone: "0800 059 0115",
  },
};

export function getService(slug: string): Service | undefined {
  return SERVICES.find((s) => s.slug === slug);
}
